import React, { useEffect } from 'react';
import { Box, Button, CircularProgress, Stack, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { RHFPhoneInput } from '@billda/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useGetCompanyDetails,
  useUpsertCompanyDetails,
} from '../hooks/useCompanyDetails';
import {
  companyDetailsSchema,
  CompanyDetailsFormData,
  defaultCompanyDetailsValues,
} from '../types/companyDetailsSchema';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

interface CompanyDetailsFormProps {
  readOnly?: boolean;
}

export const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = ({
  readOnly = false,
}) => {
  const { data: companyDetails } = useGetCompanyDetails();
  const upsertMutation = useUpsertCompanyDetails();
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm<CompanyDetailsFormData>({
    defaultValues: defaultCompanyDetailsValues,
    resolver: yupResolver(companyDetailsSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    if (companyDetails) {
      reset({
        companyName: companyDetails.companyName,
        companyNumber: companyDetails.companyNumber,
        addressLine1: companyDetails.addressLine1,
        addressLine2: companyDetails.addressLine2,
        city: companyDetails.city,
        county: companyDetails.county,
        postcode: companyDetails.postcode,
        country: companyDetails.country,
        companyEmail: companyDetails.companyEmail,
        companyPhone: companyDetails.companyPhone,
      });
    }
  }, [companyDetails, reset]);

  const onSubmit = (data: CompanyDetailsFormData) => {
    upsertMutation.mutate(
      {
        companyName: data.companyName,
        companyNumber: data.companyNumber || null,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2 || null,
        city: data.city,
        county: data.county || null,
        postcode: data.postcode,
        country: data.country || null,
        companyEmail: data.companyEmail,
        companyPhone: data.companyPhone,
      },
      {
        onSuccess: () => {
          showSnackbar('Company details saved', 'success');
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2.5}>
        <Controller
          name="companyName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Company Name"
              fullWidth
              required={!readOnly}
              error={!readOnly && !!errors.companyName}
              helperText={!readOnly ? errors.companyName?.message : undefined}
              slotProps={{ input: { readOnly } }}
              data-testid="company-name-input"
            />
          )}
        />

        <Controller
          name="companyNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Company Number"
              fullWidth
              error={!readOnly && !!errors.companyNumber}
              helperText={!readOnly ? errors.companyNumber?.message : undefined}
              slotProps={{ input: { readOnly } }}
              data-testid="company-number-input"
            />
          )}
        />

        <Controller
          name="addressLine1"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Address Line 1"
              fullWidth
              required={!readOnly}
              error={!readOnly && !!errors.addressLine1}
              helperText={!readOnly ? errors.addressLine1?.message : undefined}
              slotProps={{ input: { readOnly } }}
              data-testid="address-line1-input"
            />
          )}
        />

        <Controller
          name="addressLine2"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              label="Address Line 2"
              fullWidth
              error={!readOnly && !!errors.addressLine2}
              helperText={!readOnly ? errors.addressLine2?.message : undefined}
              slotProps={{ input: { readOnly } }}
              data-testid="address-line2-input"
            />
          )}
        />

        <Stack direction="row" spacing={2}>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="City"
                fullWidth
                required={!readOnly}
                error={!readOnly && !!errors.city}
                helperText={!readOnly ? errors.city?.message : undefined}
                slotProps={{ input: { readOnly } }}
                data-testid="city-input"
              />
            )}
          />

          <Controller
            name="county"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="County"
                fullWidth
                error={!readOnly && !!errors.county}
                helperText={!readOnly ? errors.county?.message : undefined}
                slotProps={{ input: { readOnly } }}
                data-testid="county-input"
              />
            )}
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Controller
            name="postcode"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Postcode"
                fullWidth
                required={!readOnly}
                error={!readOnly && !!errors.postcode}
                helperText={!readOnly ? errors.postcode?.message : undefined}
                slotProps={{ input: { readOnly } }}
                data-testid="postcode-input"
              />
            )}
          />

          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label="Country"
                fullWidth
                error={!readOnly && !!errors.country}
                helperText={!readOnly ? errors.country?.message : undefined}
                slotProps={{ input: { readOnly } }}
                data-testid="country-input"
              />
            )}
          />
        </Stack>

        <Stack direction="row" spacing={2}>
          <Controller
            name="companyEmail"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Company Email"
                fullWidth
                required={!readOnly}
                type="email"
                error={!readOnly && !!errors.companyEmail}
                helperText={
                  !readOnly ? errors.companyEmail?.message : undefined
                }
                slotProps={{ input: { readOnly } }}
                data-testid="company-email-input"
              />
            )}
          />

          <RHFPhoneInput
            name="companyPhone"
            control={control}
            label="Company Phone"
            fullWidth
            required={!readOnly}
            disabled={readOnly}
            data-testid="company-phone-input"
          />
        </Stack>

        {!readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || !isDirty || upsertMutation.isPending}
              data-testid="save-company-details-button"
            >
              {upsertMutation.isPending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </Box>
        )}
      </Stack>
    </form>
  );
};
